import seedRandom from 'seed-random';

const brandedAvatars = [
  'brand_pattern_01',
  'brand_pattern_02',
  'brand_pattern_03',
  'brand_pattern_04',
  'brand_pattern_05',
  'brand_pattern_06',
  'brand_pattern_07',
  'brand_pattern_08',
  'brand_pattern_09',
  'brand_pattern_10',
  'brand_pattern_11',
  'brand_pattern_12',
  'brand_pattern_13',
  'brand_pattern_14',
  'brand_pattern_15',
  'brand_pattern_16',
  'brand_pattern_17',
  'brand_pattern_18',
  'brand_pattern_19',
  'brand_pattern_20',
];

export const avatarNames = [
  'abstract_frog',
  'abstract_lion',
  'abstract_pig',
  'abstract_rabbit',
  'avocado',
  'baked_potato',
  'baseball',
  'basketball',
  'bear',
  'black_cat',
  'blue_robot',
  'bunny',
  'burger',
  'cake',
  'comet',
  'croissant',
  'donut',
  'earth',
  'felix',
  'football',
  'ice_cream',
  'leaf',
  'moon',
  'orange_cat',
  'parrot',
  'pepperoni',
  'pineapple',
  'red_robot',
  'retriever',
  'ruby',
  'soccerball',
  'sun',
  't_rex',
  'taco',
  'tennisball',
  'unicorn',
  'Leila',
  ...brandedAvatars,
];

export interface AvatarOption {
  name: string;
  image: string;
  blink: string;
  backgroundColor: string;
  category: string;
}

export const INEA_PERSON_ID = 'inea-agent';
export const INEA_AVATAR_NAME =
  'dicebear:avataaars:seed=inea-agent&topType=LongHairNotTooLong&accessoriesType=Blank&hairColor=PastelPink&facialHairType=Blank&clotheType=GraphicShirt&clotheColor=Pink&graphicType=Hola&eyeType=Default&eyebrowType=Default&mouthType=Default&skinColor=Light';

const shortHairTopTypes = [
  'ShortHairDreads01',
  'ShortHairDreads02',
  'ShortHairFrizzle',
  'ShortHairShaggyMullet',
  'ShortHairShortCurly',
  'ShortHairShortFlat',
  'ShortHairShortRound',
  'ShortHairShortWaved',
  'ShortHairSides',
  'ShortHairTheCaesar',
  'ShortHairTheCaesarSidePart',
];

const longHairTopTypes = [
  'LongHairBigHair',
  'LongHairBob',
  'LongHairBun',
  'LongHairCurly',
  'LongHairCurvy',
  'LongHairDreads',
  'LongHairFrida',
  'LongHairFro',
  'LongHairFroBand',
  'LongHairNotTooLong',
  'LongHairShavedSides',
  'LongHairMiaWallace',
  'LongHairStraight',
  'LongHairStraight2',
  'LongHairStraightStrand',
];

const hairColors = ['Black', 'Brown', 'BrownDark', 'Blonde', 'Auburn', 'PastelPink', 'SilverGray'];
const facialHairTypes = ['Blank', 'Blank', 'Blank', 'BeardLight', 'MoustacheFancy'];
const clotheTypes = ['BlazerShirt', 'CollarSweater', 'Hoodie', 'Overall', 'ShirtCrewNeck', 'ShirtScoopNeck'];
const clotheColors = ['Blue01', 'Blue02', 'Blue03', 'Gray01', 'PastelBlue', 'PastelGreen', 'PastelOrange', 'Pink', 'Red'];
const clotheGraphicTypes = ['Bat', 'Cumbia', 'Deer', 'Diamond', 'Hola', 'Pizza', 'Resist', 'Selena', 'Skull', 'SkullOutline'];
const eyeTypes = ['Default', 'Happy', 'Wink', 'Squint'];
const eyebrowTypes = ['Default', 'DefaultNatural', 'RaisedExcited', 'UpDown'];
const mouthTypes = ['Default', 'Smile', 'Twinkle'];
const skinColors = ['Light', 'Pale', 'Brown', 'DarkBrown', 'Tanned'];

const pickAvatarOption = <T>(rng: () => number, options: T[]) =>
  options[Math.floor(rng() * options.length)] ?? options[0];

// Takes in the avatar section to select from
// and the user id and returns an avatar
export const getAvatarFromUserId = (userId: string) => {
  const source = userId || '0';
  if (source === INEA_PERSON_ID) return INEA_AVATAR_NAME;

  const rng = seedRandom(source);
  const seed = `${source}-${Math.floor(rng() * 10_000_000)}`;
  const useLongHair = rng() >= 0.5;
  const params = new URLSearchParams({
    seed,
    topType: pickAvatarOption(rng, useLongHair ? longHairTopTypes : shortHairTopTypes),
    accessoriesType: 'Blank',
    hairColor: pickAvatarOption(rng, hairColors),
    facialHairType: useLongHair ? 'Blank' : pickAvatarOption(rng, facialHairTypes),
    facialHairColor: pickAvatarOption(rng, hairColors),
    clotheType: pickAvatarOption(rng, clotheTypes),
    clotheColor: pickAvatarOption(rng, clotheColors),
    clotheGraphicType: pickAvatarOption(rng, clotheGraphicTypes),
    eyeType: pickAvatarOption(rng, eyeTypes),
    eyebrowType: pickAvatarOption(rng, eyebrowTypes),
    mouthType: pickAvatarOption(rng, mouthTypes),
    skinColor: pickAvatarOption(rng, skinColors),
  });

  return `dicebear:avataaars:${params.toString()}`;
};

export const DICEBEAR_STYLES = [
  { id: 'adventurer', name: 'Adventurer', family: 'adventurer' },
  { id: 'adventurer-neutral', name: 'Adventurer Neutral', family: 'adventurer' },
  { id: 'avataaars', name: 'Avataaars', family: 'avataaars' },
  { id: 'avataaars-neutral', name: 'Avataaars Neutral', family: 'avataaars' },
  { id: 'lorelei', name: 'Lorelei', family: 'lorelei' },
  { id: 'lorelei-neutral', name: 'Lorelei Neutral', family: 'lorelei' },
  { id: 'personas', name: 'Persona', family: 'personas' },
];

export const DICEBEAR_MOUTH_MAPPINGS: Record<string, { idle: string; talk: string }> = {
  avataaars: { idle: 'smile', talk: 'twinkle' },
  'avataaars-neutral': { idle: 'smile', talk: 'twinkle' },
  bottts: { idle: 'smile01', talk: 'smile02' },
  'open-peeps': { idle: 'smile', talk: 'open' },
  lorelei: { idle: 'happy01', talk: 'happy12' },
  'lorelei-neutral': { idle: 'happy01', talk: 'happy12' },
  adventurer: { idle: 'variant07', talk: 'variant13' },
  'adventurer-neutral': { idle: 'variant07', talk: 'variant13' },
  personas: { idle: 'smile', talk: 'bigSmile' },
};
